import { MonitoringCategory } from "@domain/product/monitoring/monitoring-product.service";
import { FloorplanPlaceholderPositionPrefix } from "@domain/project/floorplan/floorplan-placeholder-position-kind";
import { PlaceholderProduct } from "@domain/project/floorplan/placeholder-product";
import { MonitoringConfigProductMap } from "@domain/project/monitoring-product-list/monitoring-config-product-map";

interface Section<TDevice, TPlaceholder, TConfiguration, TMapping extends MonitoringConfigProductMap> {
  devices: TDevice[];
  placeholders: TPlaceholder[];
  mappings: TMapping[];

  getConfigurations(): TConfiguration[];

  getUniqueConfigurations(): TConfiguration[];

  getPlacedConfigurationCount(config: TConfiguration): number;
}

export class MonitoringCategorySection<
  TDevice extends { config: TConfig },
  TPlaceholder extends { id: string; products: PlaceholderProduct[] },
  TConfig extends { id: string },
  TMapping extends MonitoringConfigProductMap,
> implements Section<TDevice, TPlaceholder, TConfig, TMapping> {
  private readonly CENTRAL_STATION_PLACEHOLDER_POSITION_PREFIX: string = this.trimPlaceholderPrefix(
    FloorplanPlaceholderPositionPrefix.CENTRAL_STATION,
  );
  private readonly DISPLAY_PLACEHOLDER_POSITION_PREFIX: string = "DP";
  private readonly KVMS_PLACEHOLDER_POSITION_PREFIX: string = "KS";
  private readonly PATIENT_MONITOR_PLACEHOLDER_POSITION_PREFIX: string = this.trimPlaceholderPrefix(
    FloorplanPlaceholderPositionPrefix.PATIENT_MONITOR,
  );
  private readonly TELEMETRY_PLACEHOLDER_POSITION_PREFIX: string = this.trimPlaceholderPrefix(
    FloorplanPlaceholderPositionPrefix.TELEMETRY,
  );
  private readonly UTILITIES_PLACEHOLDER_POSITION_PREFIX: string = "UT";

  placeholderPositionPrefix: string;
  category: string;

  constructor(
    public devices: TDevice[],
    public placeholders: TPlaceholder[],
    public mappings: TMapping[] = [],
    category: string,
  ) {
    this.category = category;
    this.placeholderPositionPrefix = this.getPlaceholderPositionPrefix();
  }
  getConfigurations(): TConfig[] {
    return this.devices.map((d) => d.config);
  }

  getUniqueConfigurations(): TConfig[] {
    return Array.from(new Map(this.getConfigurations().map((c) => [c.id, c])).values());
  }

  getPlacedConfigurationCount(config: TConfig): number {
    return this.devices.filter((device) => device.config.id === config.id).length;
  }

  private getPlaceholderPositionPrefix() {
    switch (this.category) {
      case MonitoringCategory.CENTRAL_STATIONS:
        return this.CENTRAL_STATION_PLACEHOLDER_POSITION_PREFIX;
      case MonitoringCategory.DISPLAYS:
        return this.DISPLAY_PLACEHOLDER_POSITION_PREFIX;
      case MonitoringCategory.KVMS:
        return this.KVMS_PLACEHOLDER_POSITION_PREFIX;
      case MonitoringCategory.PATIENT_MONITORS:
        return this.PATIENT_MONITOR_PLACEHOLDER_POSITION_PREFIX;
      case MonitoringCategory.TELEMETRY:
        return this.TELEMETRY_PLACEHOLDER_POSITION_PREFIX;
      case MonitoringCategory.UTILITY:
        return this.UTILITIES_PLACEHOLDER_POSITION_PREFIX;
    }
    return "";
  }

  private trimPlaceholderPrefix(prefix: FloorplanPlaceholderPositionPrefix) {
    return prefix.replace(".", "");
  }
}
